import { Box, Button } from '@mui/material';
import { useFormSectionContext } from 'components/form';
import { useAuth } from 'hooks';
import { useFormState } from 'hooks/useFormState';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { DevicePictureTypeId, PhysicalDeviceInput } from 'types';
import { removePicture, uploadPicture } from './firebase';
import styles from './PictureCapture.module.scss';
import { useUi } from 'hooks/useUi';
declare global {
    interface Navigator {
        userAgentData?: {
            mobile: boolean;
        };
    }
}

interface PictureCaptureProps {
    field: string;
    pictureTypeId: DevicePictureTypeId;
}

type FacingModeOptions = 'environment' | 'user';

export function PictureCapture({ field, pictureTypeId }: PictureCaptureProps) {
    const { ui } = useUi();
    const { user } = useAuth();
    const { setValue, watch } = useFormContext();
    const { setFormSectionError } = useFormState();
    const { formSection } = useFormSectionContext();

    const [isPhoto, setIsPhoto] = useState(false);
    const [facingMode, setFacingMode] =
        useState<FacingModeOptions>('environment');

    const photoRef = useRef<HTMLImageElement>(null);
    const videoRef = useRef<HTMLVideoElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const startButtonRef = useRef<HTMLButtonElement>(null);

    const supplierId = user?.protected?.supplierId;
    const formValues = watch() as PhysicalDeviceInput;
    const pictureFieldValue = watch(field);

    const [hasCameraPermission, setHasCameraPermission] = useState(false);

    useEffect(() => {
        // The width and height of the captured photo. We will set the
        // width to the value defined here, but the height will be
        // calculated based on the aspect ratio of the input stream.

        const width = 320; // We will scale the photo width to this
        let height = 0; // This will be computed based on the input stream

        // streaming indicates whether or not we're currently streaming
        // video from the camera. Obviously, we start at false.

        let streaming = false;

        function startup() {
            if (navigator.mediaDevices?.getUserMedia && window) {
                const video = videoRef.current;
                const canvas = canvasRef.current;
                const startButton = startButtonRef.current;
                const photo = photoRef.current;

                if (video && canvas && startButton) {
                    navigator.mediaDevices
                        .getUserMedia({
                            video: {
                                facingMode,
                            },
                            audio: false,
                        })
                        .then((stream) => {
                            video.srcObject = stream;
                            video.play();
                        });

                    video.addEventListener(
                        'canplay',
                        () => {
                            if (!streaming) {
                                height =
                                    video.videoHeight /
                                    (video.videoWidth / width);

                                // Firefox currently has a bug where the height can't be read from
                                // the video, so we will make assumptions if this happens.

                                if (isNaN(height)) {
                                    height = width / (4 / 3);
                                }

                                video.setAttribute('width', width.toString());
                                video.setAttribute('height', height.toString());
                                // for ios compatibility
                                video.setAttribute('autoplay', '');
                                video.setAttribute('muted', '');
                                video.setAttribute('playsinline', '');
                                canvas.setAttribute('width', width.toString());
                                canvas.setAttribute(
                                    'height',
                                    height.toString()
                                );
                                streaming = true;
                            }
                        },
                        false
                    );
                    if (pictureFieldValue.url?.length && photo) {
                        photo.setAttribute('src', pictureFieldValue.url);
                        setIsPhoto(true);
                    }
                }
            }
        }

        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            return;
        }

        navigator.mediaDevices
            .getUserMedia({ video: { facingMode }, audio: false })
            .then(() => {
                setHasCameraPermission(true);
            })
            .catch(() => {
                setHasCameraPermission(false);
            });

        if (hasCameraPermission) {
            startup();
        }

        const currentVideoRef = videoRef?.current;

        return () => {
            if (currentVideoRef) {
                const stream = currentVideoRef.srcObject as MediaStream;
                const tracks = stream?.getTracks();

                if (tracks) {
                    tracks.forEach((track) => {
                        track.stop();
                    });
                }
            }
        };
    }, [hasCameraPermission, facingMode, pictureFieldValue]);

    const takePictureHandler = useCallback(() => {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        const photo = photoRef.current;

        if (video && canvas && photo) {
            const hasPhoto = !!pictureFieldValue.path?.length;

            const context = canvas.getContext('2d');
            // if there is no photo in the form state, add a new photo
            if (!hasPhoto) {
                canvas.width = 400;

                canvas.height = 400;
                context?.drawImage(video, 0, 0, 400, 400);

                canvas.toBlob(async (blob) => {
                    if (supplierId !== undefined && blob) {
                        const pictureRes = await uploadPicture(
                            supplierId,
                            formValues.deviceId,
                            pictureTypeId,
                            blob
                        );

                        if (pictureRes) {
                            const { url } = pictureRes;
                            photo.setAttribute('src', url);
                            setValue(field, pictureRes);
                            setIsPhoto(true);
                        }
                    }
                });
            } else {
                if (supplierId !== undefined) {
                    removePicture(pictureFieldValue.fullPath);
                    photo.setAttribute('src', '');
                    setValue(field, { url: '', path: '' });
                    setIsPhoto(false);
                }
            }
        }
    }, [
        field,
        formValues.deviceId,
        pictureFieldValue,
        pictureTypeId,
        setValue,
        supplierId,
    ]);

    useEffect(() => {
        setFormSectionError({
            section: formSection,
            field: {
                name: field,
                error: !!pictureFieldValue?.url,
            },
        });
    }, [field, pictureFieldValue, setFormSectionError, formSection]);

    return (
        <Box className={styles.container}>
            <div className={styles.mediaContainer}>
                <video
                    id="video"
                    ref={videoRef}
                    className={`${styles.video} ${
                        isPhoto ? styles.hidden : ''
                    }`}
                >
                    Video stream not available.
                </video>
                <canvas id="canvas" className={styles.canvas} ref={canvasRef} />
                <img
                    id="photo"
                    ref={photoRef}
                    className={`${styles.photo} ${
                        !isPhoto ? styles.hidden : ''
                    }`}
                    alt="The screen capture will appear in this box."
                />
            </div>
            <div className={styles.buttonsContainer}>
                {(navigator.userAgentData?.mobile || ui.isMobile) && (
                    <Button
                        id="facingModeButton"
                        variant="contained"
                        color="info"
                        ref={startButtonRef}
                        onClick={() =>
                            setFacingMode(
                                facingMode === 'environment'
                                    ? 'user'
                                    : 'environment'
                            )
                        }
                    >
                        Flip Camera
                    </Button>
                )}
                <Button
                    id="startButton"
                    variant="contained"
                    ref={startButtonRef}
                    onClick={takePictureHandler}
                >
                    {isPhoto ? 'Retake' : 'Take'} photo
                </Button>
            </div>
        </Box>
    );
}
